"use client";

import React, { createContext, useCallback, useContext, useState } from "react";
import ErrorBoundary from "@shared/src/components/shared/ErrorBoundary";

export type ErrorType = Error | string | null;

export interface ErrorItem {
  error: ErrorType;
  options?: {
    permanent?: boolean;
    successMessage?: string;
    errorMessage?: string;
    onReload?: () => void;
  };
}

type ErrorContextType = {
  addError: (error: ErrorItem) => void;
  clearErrors: () => void;
  errors: ErrorItem[];
};

const getOrCreateContext = (): React.Context<ErrorContextType | undefined> => {
  const GLOBAL_KEY = "__IX_ErrorContext__" as const;
  if (!(GLOBAL_KEY in globalThis)) {
    (globalThis as any)[GLOBAL_KEY] = React.createContext<ErrorContextType | undefined>(undefined);
  }
  return (globalThis as any)[GLOBAL_KEY];
};

const ErrorContext = getOrCreateContext();


export function useErrorContext() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error("useErrorContext must be used within an ErrorProvider");
  }
  return context;
}

export function ClientErrorBoundary() {
  const { errors } = useErrorContext();
  return <ErrorBoundary errors={errors} />;
}

export function ErrorProvider({ children }: { children: React.ReactNode }) {
  const [errors, setErrors] = useState<ErrorItem[]>([]);

  const addError = useCallback((error: ErrorItem) => {
    setErrors((prev) => [...prev, error]);
  }, []);

  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);

  return (
    <ErrorContext.Provider value={{ errors, addError, clearErrors }}>
      <ClientErrorBoundary />
      {children}
    </ErrorContext.Provider>
  );
}
